import * as React from "react";
import { homeLink, request } from "./../../../../../services/cpln";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { Button, Card, Divider, Space, Tooltip } from "antd";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { ProviderNetworking } from "../../../advanced/providerNetworking";
import { TritonLoadBalancer } from "./loadBalancer";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpSwitch } from "../../../../gvc/testNewForm/CpSwitch";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { store } from "../../../../../store";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpDirtyIndicatorArrayIcon } from "../../../../gvc/testNewForm/CpDirtyIndicatorArrayIconLink";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { parseLink } from "../../../../../utils/linkParser/linkParser";

interface Props {}
export const TritonProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const [locationOptions, setLocationOptions] = React.useState<any[]>([]);

  React.useEffect(() => {
    fetchSchemaData();
  }, []);

  async function fetchSchemaData() {
    // Locations
    try {
      const { data: schemaRes } = await request({ url: "/catalog/schema/mk8s" });
      const locations: string[] = schemaRes.keys.spec.keys.provider.keys.triton.keys.location.allow;
      setLocationOptions(locations.map((v) => ({ label: v, value: v })));
    } catch (e) {
      console.error("Failed to get locations from mk8s catalog schema");
    }
  }

  const [browserKey, setBrowserKey] = React.useState("");
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const privateNetworkId = useCpFormValue(`triton.privateNetworkId`);
  const imageId = useCpFormValue(`triton.imageId`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Triton Provider
            <CpErrorWarningArrayIcon
              names={[
                `triton.connection`,
                `triton.location`,
                `triton.privateNetworkId`,
                `triton.firewallEnabled`,
                `triton.loadBalancer`,
                `triton.imageId`,
                `triton.sshKeys`,
                `triton.autoscaler`,
                `triton.networking`,
                `triton.preInstallScript`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <Divider orientation="left" orientationMargin={0}>
              Connection
            </Divider>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`triton.connection.privateKeySecretLink`}>Triton SSH Private Key Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `An opaque secret that contains your Triton SSH Private Key, it will be used to manage servers in your Triton account`,
                  }}
                />
              </CpLabelTooltip>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`triton.connection.privateKeySecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "opaque" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-triton-create-secret`,
                        "Create Opaque Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `triton.connection.privateKeySecretLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`opaque`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`triton.connection.privateKeySecretLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`triton.connection.url`}>URL</CpLabel>
              <CpInput name={`triton.connection.url`} style={{ width: 600 }} />
              <CpErrorWarning name={`triton.connection.url`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`triton.connection.account`}>Account</CpLabel>
              <CpInput name={`triton.connection.account`} style={{ width: 600 }} />
              <CpErrorWarning name={`triton.connection.account`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`triton.connection.user`}>User</CpLabel>
              <CpInput name={`triton.connection.user`} style={{ width: 600 }} />
              <CpErrorWarning name={`triton.connection.user`} />
            </CpFormItem>
            <Divider orientation="left" orientationMargin={0}>
              Settings
            </Divider>
            <CpFormItem>
              <CpLabel name={`triton.location`}>Location</CpLabel>
              <CpSelect name={`triton.location`} options={locationOptions} style={{ width: 600 }} />
              <CpErrorWarning name={`triton.location`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`triton.privateNetworkId`}>Private Network ID</CpLabel>
              <Space.Compact>
                <CpInput name={`triton.privateNetworkId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("private-networks")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`triton.privateNetworkId`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`triton.imageId`}>Image</CpLabel>
              <Space.Compact>
                <CpInput name={`triton.imageId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("images")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`triton.imageId`} />
            </CpFormItem>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`triton.firewallEnabled`}>Firewall Enabled</CpLabel>
                <CpSwitch name={`triton.firewallEnabled`} />
              </div>
              <CpErrorWarning name={`triton.firewallEnabled`} />
            </CpFormItem>
            <div style={{ maxWidth: 600 }}>
              <CpArrayString label={`SSH Keys`} name={`triton.sshKeys`} />
            </div>
          </CardContent>
          <div className={"mt-4"}>
            <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
              <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                <div
                  className={`flex items-center gap-2 color-link-hover cursor-pointer`}
                  onClick={() => {
                    setIsAdvancedCollapsed((x) => !x);
                  }}
                >
                  <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                  <span>Advanced</span>
                  <CpDirtyIndicatorArrayIcon names={["provider-triton-advanced"]} />
                </div>
              </div>
            </CardTitle>
          </div>
          <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
            <ProviderPreInstallScript provider="triton" />
            <ProviderNetworking provider={"triton"} />
            <ProviderAutoscaler provider="triton" />
            <TritonLoadBalancer />
          </CardCollapsibleContent>
        </Card>
      </div>
      {browserKey === "private-networks" ? (
        <NGProviderBrowser
          title="Browse Triton Private Networks"
          initialValue={privateNetworkId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "private-networks",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(id, `triton.privateNetworkId`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          sortable
          labels={["Name", "Id", "Description", "Subnet", "VLAN Id"]}
          getData={(item: any) => [item.id, item.name, item.id, item.description, item.subnet, item.vlan_id]}
        />
      ) : null}
      {browserKey === "images" ? (
        <NGProviderBrowser
          title="Browse Triton Images"
          initialValue={imageId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(id, `triton.imageId`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          sortable
          distinctFilters={["Type", "Brand"]}
          filteredColumns={["Name"]}
          labels={["Name", "Id", "Size", "Type", "Brand", "Version"]}
          getData={(item: any) => [
            item.id,
            item.name,
            item.id,
            [item.image_size || 0, item.image_size ? `${item.image_size / 1024}Gi` : "-"],
            item.type,
            item.requirements?.brand,
            item.version,
          ]}
        />
      ) : null}
    </>
  );
};
